import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';
import { SaveManager } from '../game/SaveManager';
import { arenaCreate, arenaUpdate, arenaCancel, arenaDetail, type CreateConfig } from '../game/arena';
import { DISCIPLINES } from '../game/data/disciplines';
import { t, disciplineLabel } from '../game/i18n';
import { addBackButton } from '../ui/BackButton';
import { Button } from '../ui/Button';
import { drawGradientBackground } from '../ui/Background';
import { onResize, viewport } from '../ui/viewport';

const DAY = 86_400_000;
const ALL_DISC = Object.keys(DISCIPLINES);
const FEES = [0, 50, 100, 250, 500];
const MAXES = [0, 8, 16, 32]; // 0 = unlimited
const DURATIONS: { key: string; days: number }[] = [
  { key: 'arena.dur1d', days: 1 }, { key: 'arena.dur3d', days: 3 }, { key: 'arena.dur7d', days: 7 },
];

/** Room builder + editor: name, events, duration, entry fee, max players, visibility. */
export class ArenaCreate extends Phaser.Scene {
  private editId?: string;
  private nameInput?: HTMLInputElement;
  private selected = new Set<string>(['sprint_100m', 'long_jump', 'high_jump']);
  private durationDays = 3;
  private fee = 100;
  private maxPlayers = 0;
  private visibility: 'public' | 'private' = 'public';
  private dyn: Phaser.GameObjects.GameObject[] = [];
  private editEntrants = 1;
  private busy = false;
  private ready = false; // gate rendering until an edit prefill has loaded

  constructor() {
    super('ArenaCreate');
  }

  init(data: { editId?: string }): void {
    this.editId = data?.editId;
  }

  create(): void {
    this.dyn = [];
    this.ready = !this.editId;
    this.build();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.nameInput?.remove());
    // IMPORTANT: only re-layout on a WIDTH (orientation) change. The mobile keyboard changes the
    // viewport HEIGHT — restarting then would wipe the half-typed name. So ignore height changes.
    const w0 = this.scale.width;
    onResize(this, () => { if (Math.abs(this.scale.width - w0) > 2) { this.nameInput?.remove(); this.scene.restart({ editId: this.editId }); } });
  }

  private build(): void {
    const { h, cx, s } = viewport(this);
    drawGradientBackground(this);
    addBackButton(this, s, () => { this.nameInput?.remove(); this.scene.start(this.editId ? 'ArenaDetail' : 'Arena', this.editId ? { tournamentId: this.editId } : undefined); });

    this.add.text(cx, h * 0.07, `${this.editId ? '✏️' : '➕'} ${this.editId ? t('arena.editTitle') : t('arena.newTitle')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(25 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    // Name (DOM input).
    const input = document.createElement('input');
    input.type = 'text'; input.maxLength = 40; input.placeholder = t('arena.name');
    Object.assign(input.style, {
      position: 'fixed', left: '50%', top: '14%', transform: 'translateX(-50%)', width: 'min(80vw, 420px)',
      padding: '9px 14px', fontSize: '17px', fontFamily: '"Twemoji Country Flags", system-ui, sans-serif',
      borderRadius: '12px', border: '2px solid #59d2ff', background: '#0b1020', color: '#f4f7ff',
      textAlign: 'center', outline: 'none', boxSizing: 'border-box', zIndex: '30',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(input);
    this.nameInput = input;

    if (this.editId && !this.ready) {
      void arenaDetail(this.editId).then((r) => {
        if (r.ok && r.data) {
          const tv = r.data.tournament;
          input.value = tv.name;
          this.selected = new Set(tv.disciplines);
          this.fee = tv.entryFee; this.maxPlayers = tv.maxPlayers; this.visibility = tv.visibility;
          this.editEntrants = tv.entrantCount;
          const days = Math.round((tv.endAt - tv.startAt) / DAY);
          this.durationDays = [1, 3, 7].reduce((best, d) => (Math.abs(d - days) < Math.abs(best - days) ? d : best), 3);
        }
        this.ready = true;
        this.renderDynamic();
      });
    }
    this.renderDynamic();
  }

  private get locked(): boolean { return !!this.editId && this.editEntrants > 1; }

  /** (Re)draw everything reflecting mutable state: event chips + option rows + actions. */
  private renderDynamic(): void {
    const { w, h, cx, s } = viewport(this);
    this.dyn.forEach((o) => o.destroy());
    this.dyn = [];
    if (!this.ready) {
      this.dyn.push(this.add.text(cx, h * 0.5, t('arena.loading'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(18 * s)}px`, color: '#9fb0e0' }).setOrigin(0.5));
      return;
    }

    const colTop = h * 0.24;
    // ── Events grid (left), 3 columns ──
    const leftX = w * 0.05, colW = w * 0.44;
    this.dyn.push(this.add.text(leftX, colTop - 18 * s, t('arena.pickDisc'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: '#9fb0e0', fontStyle: 'bold' }).setOrigin(0, 0.5));
    const cols = 3, gxd = 6 * s, cwd = (colW - gxd * (cols - 1)) / cols, chH = Math.max(26, 30 * s), gyd = 6 * s;
    ALL_DISC.forEach((d, i) => {
      const col = i % cols, row = Math.floor(i / cols);
      const x = leftX + col * (cwd + gxd), y = colTop + row * (chH + gyd);
      this.chip(x, y, cwd, chH, disciplineLabel(d), this.selected.has(d), s, this.locked ? undefined : () => {
        if (this.selected.has(d)) this.selected.delete(d); else this.selected.add(d);
        AudioEngine.tap(0.3); this.renderDynamic();
      }, Math.max(9, Math.round(10.5 * s)));
    });

    // ── Options (right): each is ONE line — label on the left, chips on the right ──
    const rightX = w * 0.53, optW = w * 0.42;
    const labelW = optW * 0.3, chipsX = rightX + labelW, chipsW = optW - labelW;
    const rowH = Math.max(28, 32 * s), vgap = Math.max(12, 16 * s);
    let oy = colTop;
    const section = (label: string, labels: string[], active: number, onPick: (i: number) => void): void => {
      this.dyn.push(this.add.text(rightX, oy + rowH / 2, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12.5 * s))}px`, color: '#9fb0e0', fontStyle: 'bold' }).setOrigin(0, 0.5));
      const n = labels.length, gx = 5 * s, cwO = (chipsW - gx * (n - 1)) / n;
      labels.forEach((lab, i) => this.chip(chipsX + i * (cwO + gx), oy, cwO, rowH, lab, i === active, s, this.locked ? undefined : () => { onPick(i); AudioEngine.tap(0.3); this.renderDynamic(); }, Math.max(10, Math.round(11.5 * s))));
      oy += rowH + vgap;
    };
    section(t('arena.duration'), DURATIONS.map((d) => t(d.key)), DURATIONS.findIndex((d) => d.days === this.durationDays), (i) => { this.durationDays = DURATIONS[i]!.days; });
    section(`${t('arena.fee')} 🪙`, FEES.map((f) => (f === 0 ? t('arena.free') : String(f))), FEES.indexOf(this.fee), (i) => { this.fee = FEES[i]!; });
    section(t('arena.maxPlayers'), MAXES.map((m) => (m === 0 ? '∞' : String(m))), MAXES.indexOf(this.maxPlayers), (i) => { this.maxPlayers = MAXES[i]!; });
    section(t('arena.visibility'), [t('arena.public'), t('arena.private')], this.visibility === 'public' ? 0 : 1, (i) => { this.visibility = i === 0 ? 'public' : 'private'; });

    if (this.locked) this.dyn.push(this.add.text(rightX, oy, t('arena.lockedNote'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(10, Math.round(11 * s))}px`, color: '#ffd24a', wordWrap: { width: optW } }).setOrigin(0, 0));

    // ── Actions ──
    const canAfford = this.editId ? true : SaveManager.instance.coins >= this.fee;
    const ok = this.selected.size >= 1 && canAfford;
    const by = h - Math.max(30, 36 * s);
    if (this.editId) {
      const half = Math.min(220, w * 0.34);
      this.dyn.push(new Button(this, cx - half / 2 - 6 * s, by, t('arena.saveChanges'), () => this.submit(), { width: half, height: Math.max(46, 52 * s), color: ok ? PALETTE.good : PALETTE.trackLine, fontSize: Math.max(14, Math.round(16 * s)) }));
      if (this.editEntrants <= 1) this.dyn.push(new Button(this, cx + half / 2 + 6 * s, by, `🗑 ${t('arena.cancelTournament')}`, () => this.cancel(), { width: half, height: Math.max(46, 52 * s), color: PALETTE.bad, fontSize: Math.max(13, Math.round(15 * s)) }));
    } else {
      const label = `${t('arena.confirmCreate')}${this.fee > 0 ? ` · ${this.fee} 🪙` : ''}`;
      this.dyn.push(new Button(this, cx, by, label, () => this.submit(), { width: Math.min(360, w * 0.6), height: Math.max(48, 54 * s), color: ok ? PALETTE.good : PALETTE.trackLine, fontSize: Math.max(15, Math.round(18 * s)) }));
      const hint = !this.selected.size ? t('arena.needDisc') : (!canAfford ? t('arena.errInsufficient') : '');
      if (hint) this.dyn.push(this.add.text(cx, by - Math.max(34, 40 * s), hint, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(13 * s)}px`, color: '#ff8f9c' }).setOrigin(0.5));
    }
  }

  private chip(x: number, y: number, cw: number, ch: number, label: string, on: boolean, s: number, onTap: (() => void) | undefined, fs?: number): void {
    const g = this.add.graphics();
    const dim = onTap ? 1 : 0.6;
    g.fillStyle(on ? PALETTE.laneAccent : PALETTE.ink, (on ? 1 : 0.5) * dim).fillRoundedRect(x, y, cw, ch, ch / 2);
    g.lineStyle(1.5, on ? PALETTE.white : PALETTE.cadence, (on ? 0.6 : 0.3) * dim).strokeRoundedRect(x, y, cw, ch, ch / 2);
    const tx = this.add.text(x + cw / 2, y + ch / 2, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs ?? Math.max(11, Math.round(12.5 * s))}px`, color: on ? '#0b1020' : '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5);
    if (tx.width > cw - 8 * s) tx.setScale((cw - 8 * s) / tx.width);
    if (onTap) g.setInteractive(new Phaser.Geom.Rectangle(x, y, cw, ch), Phaser.Geom.Rectangle.Contains).on(Phaser.Input.Events.POINTER_DOWN, onTap);
    this.dyn.push(g, tx);
  }

  private submit(): void {
    if (this.busy) return;
    if (this.selected.size < 1) { this.toast(t('arena.needDisc')); return; }
    const name = (this.nameInput?.value || '').trim() || 'Torneo';
    this.busy = true;
    AudioEngine.tap(0.5);
    const now = Date.now();
    if (this.editId) {
      const patch = this.locked
        ? { name }
        : { name, disciplines: [...this.selected], endAt: now + this.durationDays * DAY, entryFee: this.fee, maxPlayers: this.maxPlayers, visibility: this.visibility };
      void arenaUpdate(this.editId, patch).then((r) => {
        this.busy = false;
        if (!r.ok || !r.data) { this.toast(r.error === 'insufficient' ? t('arena.errInsufficient') : t('arena.offline')); return; }
        AudioEngine.chime(); SaveManager.instance.setServerCoins(r.data.coins);
        this.nameInput?.remove(); this.scene.start('ArenaDetail', { tournamentId: this.editId });
      });
      return;
    }
    if (SaveManager.instance.coins < this.fee) { this.busy = false; this.toast(t('arena.errInsufficient')); this.cameras.main.shake(160, 0.005); return; }
    const cfg: CreateConfig = {
      name, disciplines: [...this.selected], startAt: now, endAt: now + this.durationDays * DAY,
      entryFee: this.fee, maxPlayers: this.maxPlayers, visibility: this.visibility,
    };
    void arenaCreate(cfg).then((r) => {
      this.busy = false;
      if (!r.ok || !r.data) { this.toast(r.error === 'insufficient' ? t('arena.errInsufficient') : t('arena.offline')); return; }
      AudioEngine.chime(); SaveManager.instance.setServerCoins(r.data.coins);
      this.nameInput?.remove(); this.scene.start('ArenaDetail', { tournamentId: r.data.tournament.id });
    });
  }

  private cancel(): void {
    if (this.busy || !this.editId) return;
    this.busy = true;
    void arenaCancel(this.editId).then((r) => {
      this.busy = false;
      if (!r.ok) { this.toast(r.error === 'has_entrants' ? t('arena.lockedNote') : t('arena.offline')); return; }
      AudioEngine.chime(); SaveManager.instance.setServerCoins(r.data?.coins ?? null);
      this.toast(t('arena.cancelDone'));
      this.nameInput?.remove(); this.scene.start('Arena');
    });
  }

  private toast(msg: string): void {
    const { cx, h, s } = viewport(this);
    const txt = this.add.text(cx, h * 0.92, msg, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(14 * s)}px`, color: '#ff8f9c', fontStyle: 'bold', backgroundColor: '#0b1020cc', padding: { x: 12, y: 6 } }).setOrigin(0.5).setDepth(60);
    this.tweens.add({ targets: txt, alpha: 0, delay: 1800, duration: 600, onComplete: () => txt.destroy() });
  }
}
